<template>
  <Teleport to="body" data-testid="components-help-popover-help-popover-teleport">
    <div class="help-popover" data-testid="components-help-popover-help-popover-div">
      <div class="help-popover__anchor" data-testid="components-help-popover-help-popover-div-2">
        <button
          type="button"
          class="help-popover__button"
          aria-label="Help"
          @click.stop="openHelp"
         data-testid="components-help-popover-help-popover-button">
          <img src="@/assets/svg/info.svg" alt="info" data-testid="components-help-popover-help-popover-img" />
        </button>

        <button
          type="button"
          class="help-popover__hide"
          aria-label="Hide Help"
          @click.stop="hideHelp"
         data-testid="components-help-popover-help-popover-button-2">
          <SkIcon name="Close" :size="10" color="#6B7280" data-testid="components-help-popover-help-popover-sk-icon" />
        </button>
      </div>

      <div v-if="visualState === 'open'" class="help-popover__content" data-testid="components-help-popover-help-popover-div-3">
        <div class="help-popover__content-header" data-testid="components-help-popover-help-popover-div-4">
          <h2 class="help-popover__content-title" data-testid="components-help-popover-help-popover-h2">{{ helpTitle }}</h2>
          <button type="button" class="help-popover__content-close" @click.stop="closeHelp" aria-label="Close Help" data-testid="components-help-popover-help-popover-button-3">
            <component :is="icons.Minus" :size="20" color="#00000066" />
          </button>
        </div>
        <div class="help-popover__content-body" data-testid="components-help-popover-help-popover-div-5">
          <p class="help-popover__content-text" v-html="helpContent" data-testid="components-help-popover-help-popover-p" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import SkIcon from '@/components/ui/SkIcon/SkIcon.vue';
import { icons } from '@/assets/icons/icons.js';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

const { t, te } = useI18n();

const props = defineProps({
  currentModule: {
    type: String,
    default: ''
  },
  visualState: {
    type: String,
    default: 'close',
    validator: (v) => ['open', 'close'].includes(v),
  },
})

const emit = defineEmits(['update:visualState', 'requestHide']);

const helpTitle = computed(() => {
  const key = `help_popover.titles.${props.currentModule}`;
  return te(key) ? t(key) : '';
});

const helpContent = computed(() => {
  const key = `help_popover.${props.currentModule}`;
  return t(key) || '';
});

const openHelp = () => {
   emit('update:visualState', 'open');
}

const closeHelp = () => {
  emit('update:visualState', 'close');
}

const hideHelp = () => {
  emit('requestHide');
}

</script>

<style lang="scss" scoped>
.help-popover {
  position: fixed;
  right: 30px;
  bottom: 70px;
  z-index: 10;

  &__anchor {
    position: relative;
    width: 50px;
    height: 50px;
  }

  &__button {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 50px;
    padding: 0;
    cursor: pointer;
    background: #fff;
    border: 1px solid #0000001a;
    border-radius: 50%;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  }

  &__hide {
    position: absolute;
    top: -6px;
    right: -6px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    cursor: pointer;
    background: #fff;
    border: 1px solid #0000001a;
    border-radius: 50%;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.2s ease, visibility 0.2s ease;
  }

  &__anchor:hover &__hide {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  &__content {
    position: absolute;
    bottom: 0px;
    right: -5px;
    display: flex;
    flex-direction: column;
    width: 400px;
    max-height: 650px;
    background: #fff;
    border: 1px solid #0000001a;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    overflow: hidden;
    z-index: 1001;

    &-header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px;
        border-bottom: 1px solid #0000001a;
        font-size: 26px;
        font-weight: 700;
        color: #000;
    }
    &-close {
        cursor: pointer;
        background-color: #0000000D;
        border-radius: 50%;
        padding: 2px;
    }
    &-body {
        flex: 1;
        min-height: 0;
        padding: 16px;
        overflow-y: auto;
        overflow-x: hidden;
        font-size: 14px;
        font-weight: 400;
        color: #00000099;

        :deep(a) {
          color: #2563eb;
          text-decoration: underline;

          &:hover {
            color: #1d4ed8;
          }
        }
  }
}
}
</style>
